/**
 * Medication voice utilities: TTS pronunciation and ASR transcript correction.
 * Phase 1: pre-speak text rewrite for pronunciation; post-recognition fuzzy match for medication names.
 */

/** Minimum transcript length to consider for medication name correction (avoid matching "keep", "no"). */
export const MEDICATION_MATCH_MIN_LENGTH = 4;

/** Similarity threshold: 0 to 1. Above this we replace transcript with candidate name. */
export const MEDICATION_SIMILARITY_THRESHOLD = 0.85;

/**
 * Lexicon: canonical medication name (lowercase) -> spoken form for TTS.
 * Grows over time; keys normalized (trim, lowercase).
 */
export const MEDICATION_PRONUNCIATION_LEXICON = {
  lisinopril: 'liss in oh pril',
  amlodipine: 'am low di peen',
  metformin: 'met for min',
  atorvastatin: 'a tor va sta tin',
  omeprazole: 'oh mep ra zole',
  losartan: 'lo sar tan',
  gabapentin: 'ga ba pen tin',
  sertraline: 'ser tra leen',
  amoxicillin: 'a mox i sil in',
  hydrocodone: 'hy dro co done',
  metoprolol: 'me toe pro lol',
  pantoprazole: 'pan toe pra zole',
  prednisone: 'pred ni sone',
  albuterol: 'al byoo ter ol',
  levothyroxine: 'lee vo thy rox een',
  escitalopram: 'es sy tal o pram',
  fluoxetine: 'floo ox e teen',
  tramadol: 'tram a dol',
  ibuprofen: 'eye byoo pro fen',
  acetaminophen: 'a seat a min oh fen',
  naproxen: 'na prox en',
  clopidogrel: 'klo pid oh grel',
  furosemide: 'fur oh se mide',
  simvastatin: 'sim va sta tin',
  rosuvastatin: 'roo soo va sta tin',
  duloxetine: 'doo lox e teen',
  pregabalin: 'pre ga ba lin',
  tamsulosin: 'tam soo lo sin',
  tadalafil: 'ta da la fil',
  sildenafil: 'sil den a fil',
};

/** Common medication names for ASR candidate list when user list is small. */
export const COMMON_MEDICATION_NAMES = Object.keys(MEDICATION_PRONUNCIATION_LEXICON);

/**
 * Normalize string for lookup: trim, lowercase, collapse spaces.
 * @param {string} s
 * @returns {string}
 */
function normalizeForLookup(s) {
  if (s == null || typeof s !== 'string') return '';
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Apply medication pronunciation: replace known names in text with spoken form for TTS.
 * @param {string} text - Full string to speak.
 * @param {string[]} medicationNames - Names that appear in the sentence (e.g. from current med).
 * @returns {string} - Text with names replaced by lexicon spoken form when present.
 */
export function applyMedicationPronunciation(text, medicationNames) {
  if (!text || typeof text !== 'string') return text;
  if (!Array.isArray(medicationNames) || medicationNames.length === 0) return text;
  let result = text;
  for (const name of medicationNames) {
    const n = (name ?? '').toString().trim();
    if (!n) continue;
    const key = normalizeForLookup(n);
    const spoken = MEDICATION_PRONUNCIATION_LEXICON[key];
    if (spoken) {
      const re = new RegExp(escapeRegex(n), 'gi');
      result = result.replace(re, spoken);
    }
  }
  return result;
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Levenshtein distance between two strings.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function levenshtein(a, b) {
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      const cost = b[i - 1] === a[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }
  return matrix[b.length][a.length];
}

/**
 * Similarity score 0..1 (1 = identical). Based on 1 - normalized Levenshtein.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function similarity(a, b) {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  const dist = levenshtein(a, b);
  return 1 - dist / maxLen;
}

/**
 * Correct transcript for medication name: if user likely said a medication name, return canonical form.
 * Apply only when context is "expecting medication name" (e.g. editing name section).
 * @param {string} transcript - User's raw/filtered utterance.
 * @param {string[]} candidateNames - E.g. user's current medications + optional common list.
 * @returns {{ corrected: string, matchedName: string | null }}
 */
export function correctTranscriptForMedicationName(transcript, candidateNames) {
  const raw = (transcript ?? '').toString().trim();
  if (!raw) return { corrected: raw, matchedName: null };
  if (raw.length < MEDICATION_MATCH_MIN_LENGTH) return { corrected: raw, matchedName: null };
  if (!Array.isArray(candidateNames) || candidateNames.length === 0) return { corrected: raw, matchedName: null };

  const normalized = normalizeForLookup(raw);
  let bestScore = 0;
  let bestCandidate = null;

  for (const name of candidateNames) {
    const n = (name ?? '').toString().trim();
    if (!n) continue;
    const normCandidate = normalizeForLookup(n);
    const score = similarity(normalized, normCandidate);
    if (score > bestScore) {
      bestScore = score;
      bestCandidate = n;
    }
  }

  if (bestScore >= MEDICATION_SIMILARITY_THRESHOLD && bestCandidate) {
    return { corrected: bestCandidate, matchedName: bestCandidate };
  }
  return { corrected: raw, matchedName: null };
}
